import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { userRequest } from '../utils/requestMethods';
import { useStateContext } from '../contexts/ContextProvider';
import { BackButton, CustomPagination, SkeletonTable } from '../components';
import AlertNotification from '../components/common/AlertNotification';
import { FiFileText, FiRefreshCw, FiUsers, FiSearch } from 'react-icons/fi';

const customStyles = {
  headRow: {
    style: {
      backgroundColor: 'rgb(248, 250, 252)',
      fontWeight: 'bold',
    },
  },
};

const ArchivedStudents = ({ history }) => {
  const { school } = useStateContext();
  const schoolId = school?._id;

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [restoreModal, setRestoreModal] = useState({ isOpen: false, student: null });
  const [selectedClassId, setSelectedClassId] = useState('');
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (schoolId) {
      fetchArchivedStudents();
      fetchClasses();
    }
  }, [schoolId]);

  const fetchArchivedStudents = async () => {
    setLoading(true);
    try {
      const { data } = await userRequest.get(`/children?school_id=${schoolId}&is_deleted=true&limit=500`);
      setStudents(data.children || data.data || []);
    } catch {
      setAlert({ type: 'error', message: 'Failed to fetch archived students.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const { data } = await userRequest.get(`classes?code=${school?.code}`);
      setClasses(data.classes || []);
    } catch {
      console.error('Failed to fetch classes');
    }
  };

  const openRestoreModal = (student) => {
    setRestoreModal({ isOpen: true, student });
    setSelectedClassId('');
  };

  const closeRestoreModal = () => {
    setRestoreModal({ isOpen: false, student: null });
    setSelectedClassId('');
  };

  const handleRestore = async () => {
    if (!selectedClassId) return;
    setRestoring(true);
    try {
      await userRequest.patch(`/children/${schoolId}/restore/${restoreModal.student._id}`, {
        class_id: selectedClassId,
      });
      const name = `${restoreModal.student.firstname} ${restoreModal.student.lastname}`;
      setAlert({ type: 'success', message: `${name} has been restored successfully.` });
      closeRestoreModal();
      fetchArchivedStudents();
    } catch (error) {
      setAlert({ type: 'error', message: error.response?.data?.message || 'Failed to restore student.' });
    } finally {
      setRestoring(false);
    }
  };

  // An archived student has no current class, like a graduated one. The profile's
  // Result tab has its own class picker and the transcript, and the lookups that
  // don't apply to an archived student (photo, enrolment details) just stay empty.
  const openResults = (row) => {
    history.push(`/students/view/${row._id}/none`, {
      student: [row.lastname, row.firstname, row.middlename].filter(Boolean).join(' '),
      tab: 'result',
    });
  };

  const filteredStudents = students.filter(s =>
    s.firstname?.toLowerCase().includes(filterText.toLowerCase()) ||
    s.lastname?.toLowerCase().includes(filterText.toLowerCase()) ||
    s.registration_id?.toLowerCase().includes(filterText.toLowerCase())
  ).sort((a, b) => (a.lastname || '').localeCompare(b.lastname || ''));

  const columns = [
    {
      name: 'Admission No',
      selector: row => row.registration_id || '-',
      sortable: true,
    },
    {
      name: 'Surname',
      selector: row => row.lastname,
      sortable: true,
      cell: row => <span className="font-semibold">{row.lastname}</span>,
    },
    {
      name: 'First Name',
      selector: row => row.firstname,
      sortable: true,
    },
    {
      name: 'Middle Name',
      selector: row => row.middlename || '-',
    },
    {
      name: 'Gender',
      selector: row => row.gender || '-',
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openResults(row)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors whitespace-nowrap"
          >
            <FiFileText className="w-3 h-3" />
            View Results
          </button>
          <button
            type="button"
            onClick={() => openRestoreModal(row)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
          >
            <FiRefreshCw className="w-3 h-3" />
            Restore
          </button>
        </div>
      ),
      width: '240px',
    },
  ];

  return (
    <div className="m-2 mt-20 md:mx-10 md:my-0 py-2 md:py-7">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-10 min-h-screen">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <BackButton history={history} />
          <div className="h-5 w-px bg-gray-200" />
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest">Students</p>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">Archived Students</h1>
          </div>
        </div>

        {/* Alert */}
        {alert.message && (
          <div className="mb-4">
            <AlertNotification
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert({ type: '', message: '' })}
            />
          </div>
        )}

        {/* Banner */}
        {!loading && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl mb-6">
            <FiUsers className="text-amber-500 text-xl shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                {students.length} Archived Student{students.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-amber-600">
                Restore a student to assign them back to an active class.
              </p>
            </div>
          </div>
        )}

        {/* Table card */}
        <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <div className="relative max-w-xs">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or admission no..."
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredStudents}
            pagination
            paginationComponent={CustomPagination}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            highlightOnHover
            striped
            noDataComponent={
              <div className="flex flex-col items-center py-16 text-center">
                <FiUsers className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">No archived students found</p>
              </div>
            }
          />
        </div>
      </div>

      {/* Restore Modal */}
      {restoreModal.isOpen && restoreModal.student && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                <FiRefreshCw className="w-7 h-7 text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Restore Student</h2>
              <p className="text-sm text-gray-500 mb-5">
                Select a class to restore{' '}
                <span className="font-semibold text-gray-700">
                  {restoreModal.student.firstname} {restoreModal.student.lastname}
                </span>{' '}
                into.
              </p>

              <select
                className="w-full bg-white border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 px-3 py-2.5 mb-5"
                value={selectedClassId}
                onChange={e => setSelectedClassId(e.target.value)}
              >
                <option value="">Select a class</option>
                {classes.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.class_name}</option>
                ))}
              </select>

              <div className="flex w-full gap-3">
                <button
                  onClick={closeRestoreModal}
                  disabled={restoring}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRestore}
                  disabled={!selectedClassId || restoring}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold text-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {restoring ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivedStudents;
